/**
 * @file Dashboard.js
 * @description View principal que exibe as estatísticas e classificações.
 */
import React, { useState } from "react";
import { Users, Shield, BarChart2, FileDown, Star } from "lucide-react";
import TabelaClassificacaoGeral from "../components/TabelaGeral";
import { getEscudoUrl } from "../api/ligaApi";

const StatCard = ({ icon, title, value, color }) => (
  <div className="bg-gray-800 p-4 rounded-lg shadow-md flex items-center">
    <div className={`p-3 rounded-full mr-4 ${color}`}>{icon}</div>
    <div>
      <p className="text-sm text-gray-400">{title}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  </div>
);

const Dashboard = ({
  ligaData,
  handleGerarPDF,
  handleGerarPDFGeral,
  isAdmin,
}) => {
  const { equipes, rodadas } = ligaData;

  const [selectedRodada, setSelectedRodada] = useState(isAdmin ? 1 : 1);

  const rodadasDisponiveis = isAdmin
    ? [...Array(38).keys()].map((i) => i + 1)
    : [...Array(19).keys()].map((i) => i + 1);

  const rodadasComPontos = rodadas.filter((r) => r.pontos != null);
  const totalRodadasFinalizadas = [
    ...new Set(rodadasComPontos.map((r) => r.numero)),
  ].length;

  const getPontosPorTimeNaRodada = (timeId, rodadaNum) => {
    const ponto = rodadas.find(
      (r) =>
        r.numero === rodadaNum &&
        r.time_id &&
        r.time_id.toString() === timeId.toString(),
    );
    return ponto ? ponto.pontos : 0;
  };

  const getPontosPorEquipeNaRodada = (equipe, rodadaNum) =>
    equipe.times.reduce(
      (total, time) => total + getPontosPorTimeNaRodada(time.id, rodadaNum),
      0,
    );

  const equipesPorRodada = [...equipes]
    .map((equipe) => ({
      ...equipe,
      pontosNaRodada: getPontosPorEquipeNaRodada(equipe, selectedRodada),
    }))
    .sort((a, b) => b.pontosNaRodada - a.pontosNaRodada);

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-teal-400">
        Painel da Competição
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={<Users size={24} />}
          title="Equipes na Disputa"
          value={equipes.length}
          color="bg-blue-500"
        />
        <StatCard
          icon={<Shield size={24} />}
          title="Total de Times"
          value={equipes.reduce((acc, equipe) => acc + equipe.times.length, 0)}
          color="bg-green-500"
        />
        <StatCard
          icon={<BarChart2 size={24} />}
          title="Rodadas Finalizadas"
          value={totalRodadasFinalizadas}
          color="bg-yellow-500"
        />
      </div>

      <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg mb-8">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 gap-4">
          <h3 className="text-xl font-bold">Classificação Geral Acumulada</h3>
          {isAdmin && (
            <button
              onClick={handleGerarPDFGeral}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <FileDown size={18} className="mr-2" />
              Gerar PDF Geral
            </button>
          )}
        </div>
        <TabelaClassificacaoGeral equipes={equipes} rodadasData={rodadas} />
      </div>

      <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
          <h3 className="text-xl font-bold">Detalhes da Rodada</h3>
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            <select
              value={selectedRodada}
              onChange={(e) => setSelectedRodada(Number(e.target.value))}
              className="bg-gray-700 text-white p-2 rounded-md w-full"
            >
              {rodadasDisponiveis.map((rodadaNum) => (
                <option key={rodadaNum} value={rodadaNum}>
                  Rodada {rodadaNum}
                </option>
              ))}
            </select>
            {isAdmin && (
              <button
                onClick={() => handleGerarPDF(selectedRodada)}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors text-sm sm:text-base justify-center"
              >
                <FileDown size={18} className="mr-2" />
                <span>PDF</span>
              </button>
            )}
          </div>
        </div>
        <div className="bg-gray-800">
          <div className="space-y-6">
            {equipesPorRodada.map((equipe) => (
              <div key={equipe.id} className="bg-gray-900/70 p-4 rounded-lg">
                {/* CABEÇALHO DA EQUIPE */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                  {/* Agrupamento: Logo + Nome + Estrela */}
                  <div className="flex items-center gap-3 mb-2 sm:mb-0">
                    <img
                      src={
                        getEscudoUrl(equipe.logo_url) ||
                        "/logo-equipe-padrao.png"
                      }
                      alt=""
                      className="w-10 h-10 rounded-full object-contain"
                    />

                    <h4 className="text-lg font-bold text-teal-300">
                      {equipe.nome}
                    </h4>

                    {/* Estrela de Títulos */}
                    {equipe.titulos > 0 && (
                      <div
                        className="relative flex items-center justify-center w-6 h-6 ml-2"
                        title={`${equipe.titulos} Títulos`}
                      >
                        <Star
                          size={24}
                          className="text-yellow-400 fill-yellow-400"
                        />
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-900 pt-[1px]">
                          {equipe.titulos}
                        </span>
                      </div>
                    )}
                  </div>

                  <span className="text-2xl font-bold text-white">
                    {equipe.pontosNaRodada.toFixed(2)} pts
                  </span>
                </div>

                {/* LISTA DE TIMES */}
                <div className="space-y-2">
                  {equipe.times
                    .sort((a, b) => a.nome_time.localeCompare(b.nome_time))
                    .map((time) => (
                      <div
                        key={time.id}
                        className="bg-gray-700/50 p-3 rounded-lg flex items-center justify-between"
                      >
                        {/* Lado Esquerdo: Escudo + Nome + Cartoleiro (com truncate) */}
                        <div className="flex items-center flex-1 min-w-0 mr-2">
                          <img
                            src={getEscudoUrl(time.escudo_url)}
                            alt={`Escudo do ${time.nome_time}`}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-3 flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="font-bold text-white text-sm sm:text-base flex items-center truncate">
                              <span className="truncate">{time.nome_time}</span>
                              {time.is_captain && (
                                <span className="ml-2 text-yellow-400 font-bold text-xs flex-shrink-0">
                                  (C)
                                </span>
                              )}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400 truncate">
                              {time.nome_cartoleiro}
                            </p>
                          </div>
                        </div>

                        {/* Pontuação (fixa à direita) */}
                        <p className="text-base sm:text-lg font-bold text-teal-400 whitespace-nowrap flex-shrink-0">
                          {getPontosPorTimeNaRodada(
                            time.id,
                            selectedRodada,
                          ).toFixed(2)}{" "}
                          pts
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
